import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, first, map } from "rxjs";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { IPermissionsStoreState } from "@gtmhub/users/redux/models";
import { RequestConfig } from "@webapp/core/abstracts/models/request-config.model";
import { BaseFacade } from "@webapp/core/abstracts/services/base-facade.service";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { IUsersWithAllowedActionPerItem, Permission, PermissionDTO } from "@webapp/permissions/models/permissions.model";
import { PermissionsState } from "@webapp/permissions/services/permissions-state.service";
import { IAllowedActionsMap, IUserItemAllowedActions } from "@webapp/sessions/models/sessions.model";
import { PermissionsApiService } from "./permissions-api.service";

@Injectable({
  providedIn: "root",
})
export class PermissionsFacade extends BaseFacade<Permission, PermissionDTO, PermissionsState, PermissionsApiService> {
  private permissionsSubject = new BehaviorSubject<Set<Permission>>(new Set<Permission>());

  constructor(state: PermissionsState, api: PermissionsApiService) {
    super(state, api);
    new ReduxStoreObserver(reduxStoreContainer.reduxStore)
      .whenFetched$<IPermissionsStoreState>("permissions")
      .pipe(first())
      .subscribe((state) => {
        this.permissionsSubject.next(state.permissions.items);
      });
  }

  public hasPermission$(permission: Permission): Observable<boolean> {
    return this.getPermissions$().pipe(map((permissions) => permissions.has(permission)));
  }

  public hasMultiplePermissions$(permissionsToCheck: Permission[]): Observable<boolean> {
    return this.getPermissions$().pipe(
      map((permissions) => {
        return permissionsToCheck.every((permission) => permissions.has(permission));
      })
    );
  }

  public getPermissions$(): Observable<Set<Permission>> {
    return this.permissionsSubject.asObservable();
  }

  public getCurrentUserAllowedActionsPerItem$(params: { targetId: string; targetType: string }): Observable<IUserItemAllowedActions> {
    return this.get$<IUserItemAllowedActions>(null, {
      ...new RequestConfig(),
      url: this.apiService.getCurrentUserAllowedActionsPerItemEndpoint(params.targetId),
      queryParams: { targetType: params.targetType },
    });
  }

  public getUsersWithAllowedActionPerItem$(params: {
    targetType: "goal" | "metric";
    targetId: string;
    action: keyof IAllowedActionsMap;
  }): Observable<IUsersWithAllowedActionPerItem> {
    return this.get$<IUsersWithAllowedActionPerItem>(null, {
      ...new RequestConfig(),
      url: this.apiService.getUsersWithAllowedActionPerItemEndpoint(params),
      queryParams: { action: params.action },
    });
  }
}
